
import { formatDate } from '@/lib/utils';
import { Profile, Event } from '@/lib/types/core';
import { NavLink } from 'react-router-dom';
import { ChevronRight, Mail, Clock, X } from 'lucide-react';
import { appsUserApiRouterResendVerificationEmailView, appsAdvApiRouterUnrsvpFromRide, RideOut } from '@/lib/api';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import MembershipSection from './MembershipSection';
import TicketsTab from './TicketsTab';
import { PremiumBadge } from '@/components/ui/premium-badge';

interface ProfileInfoSidebarProps {
  profile: Profile;
  userEvents: Event[];
  userRides: RideOut[];
  userRideHistory: RideOut[];
  isOwnProfile: boolean;
}

const renderSocials = () => {
  return;
  return (
    <div className="glass-card rounded-lg p-6">
      <h2 className="text-xl font-bold mb-4">Connect</h2>
      <div className="flex justify-center space-x-4">
        <button className="h-10 w-10 rounded-full bg-adv-darkGray flex items-center justify-center text-adv-lightGray hover:text-white transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
          </svg>
        </button>
        <button className="h-10 w-10 rounded-full bg-adv-darkGray flex items-center justify-center text-adv-lightGray hover:text-white transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
          </svg>
        </button>
        <button className="h-10 w-10 rounded-full bg-adv-darkGray flex items-center justify-center text-adv-lightGray hover:text-white transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path>
          </svg>
        </button>
      </div>
    </div>
  )
}

const ProfileInfoSidebar = ({ profile, userEvents, userRides, userRideHistory, isOwnProfile }: ProfileInfoSidebarProps) => {
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [unrsvpingRides, setUnrsvpingRides] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  const handleResendVerification = async () => {
    if (isResending || resendCooldown > 0) return;

    setIsResending(true);
    try {
      await appsUserApiRouterResendVerificationEmailView();
      toast({
        title: "Email sent!",
        description: "Verification email has been resent. Please check your inbox.",
      });
      
      // Start cooldown timer (3 minutes = 180 seconds)
      setResendCooldown(180);
      const timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to resend verification email';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      
      // If it's a rate limit error, set cooldown
      if (errorMessage.includes('3 minutes')) {
        setResendCooldown(180);
        const timer = setInterval(() => {
          setResendCooldown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } finally {
      setIsResending(false);
    }
  };

  const formatCooldown = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const renderRallies = (userEvents: Event[]) => {
    return userEvents.length? (
      <div className="flex justify-between">
        <span>Rides Organized</span>
        <span className="text-adv-lightGray">{userEvents.length}</span>
      </div>
    ): null;
  }

  const handleUnrsvp = async (rideId: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (unrsvpingRides.has(rideId)) return;

    try {
      setUnrsvpingRides(prev => new Set(prev).add(rideId));
      const response = await appsAdvApiRouterUnrsvpFromRide(rideId);
      toast({
        title: "You have been successfully removed from this ride.",
        description: response.data.message || "Successfully removed from ride.",
      });
      window.location.reload();
    } catch (error: any) {
      toast({
        title: "Failed to remove RSVP",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUnrsvpingRides(prev => {
        const newSet = new Set(prev);
        newSet.delete(rideId);
        return newSet;
      });
    }
  };

  const renderRidePreview = (ride: RideOut, showUnrsvp: boolean = false) => {
    const isUnrsvping = unrsvpingRides.has(ride.id);
    
    return (
      <div 
        key={ride.id} 
        className="block border border-gray-200 rounded-lg p-3 mb-2 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 shadow-sm"
      >
        <NavLink 
          to={`/ride-hub/${ride.id}`}
          className="block"
        >
          <div className="flex justify-between items-start mb-1">
            <h4 className="font-medium text-sm text-gray-900 truncate flex-1 mr-2">
              {ride.title}
            </h4>
            <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded border border-gray-200">
              {ride.ride_type}
            </span>
          </div>
          <p className="text-xs text-gray-500">
            {formatDate(ride.meeting_date, false)}
          </p>
        </NavLink>
        {showUnrsvp && (
          <Button
            variant="outline"
            className="mt-2 w-full text-xs"
            onClick={(e) => handleUnrsvp(ride.id, e)}
            disabled={isUnrsvping}
          >
            {isUnrsvping ? (
              'Removing...'
            ) : (
              <>
                <X className="w-3 h-3 mr-1" />
                Cancel Ride
              </>
            )}
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="glass-card rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4">Member Info</h2>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span>Member Type</span>
            {profile?.is_premium ? (
              <span className="flex items-center gap-1 text-adv-orange">
                <PremiumBadge size="sm" showTooltip={false} />
                Premium
              </span>
            ) : (
              <span className="text-adv-lightGray">Free</span>
            )}
          </div>
          <div className="flex justify-between">
            <span>Joined</span>
            <span className="text-adv-lightGray">{formatDate(profile?.created_at || '')}</span>
          </div>
          {renderRallies(userEvents)}
          <div className="flex justify-between">
            <span>Riding Style</span>
            <span className="text-adv-lightGray">{profile?.preferred_riding_style || 'Not specified'}</span>
          </div>
          <div className="flex justify-between">
            <span>Home Base</span>
            <span className="text-adv-lightGray">{profile?.location || 'Not specified'}</span>
          </div>
          <div className="flex justify-between">
            <span>Email Status</span>
            <span className={`${profile?.email_verified ? 'text-green-400' : 'text-yellow-400'}`}>
              {profile?.email_verified ? 'Verified' : 'Unverified'}
            </span>
          </div>
          
          {!profile?.email_verified && isOwnProfile && (
            <div className="mt-3">
              <Button
                onClick={handleResendVerification}
                disabled={isResending || resendCooldown > 0}
                variant="outline"
                className="w-full text-sm py-2"
              >
                {isResending ? (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Sending...
                  </>
                ) : resendCooldown > 0 ? (
                  <>
                    <Clock className="w-4 h-4 mr-2" />
                    Wait {formatCooldown(resendCooldown)}
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Resend Verification
                  </>
                )}
              </Button>
              <p className="text-xs text-adv-lightGray mt-1 text-center">
                Rate limited to once every 3 minutes
              </p>
            </div>
          )}

        </div>
      </div>

      <MembershipSection isOwnProfile={isOwnProfile} />

      <TicketsTab isOwnProfile={isOwnProfile} />

      {isOwnProfile && (
        <div className="glass-card rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4">Your Upcoming Rides</h2>
          
          {userRides.length > 0 ? (
            <div className="space-y-3 mb-4">
              {userRides.slice(0, 3).map(ride => renderRidePreview(ride, ride.created_by.id !== profile.id))}
              {userRides.length > 3 && (
                <p className="text-xs text-adv-lightGray text-center">
                  +{userRides.length - 3} more rides
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-adv-lightGray mb-4">
              No upcoming rides
            </p>
          )}

          <NavLink 
            to="/explore-rides" 
            className="flex items-center justify-between text-adv-orange hover:text-adv-orange/80"
          >
            Find rides near you
            <ChevronRight size={16} />
          </NavLink>
        </div>
      )}

      {isOwnProfile && (
        <div className="glass-card rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4">Your Ride History</h2>
          
          {userRideHistory.length > 0 ? (
            <div className="space-y-3 mb-4">
              {userRideHistory.slice(0, 5).map(ride => renderRidePreview(ride, false))}
              {userRideHistory.length > 5 && (
                <p className="text-xs text-adv-lightGray text-center">
                  +{userRideHistory.length - 5} more rides
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-adv-lightGray mb-4">
              No ride history yet
            </p>
          )}

          <NavLink 
            to="/ride-hub" 
            className="flex items-center justify-between text-adv-orange hover:text-adv-orange/80"
          >
            View all rides
            <ChevronRight size={16} />
          </NavLink>
        </div>
      )}

      {renderSocials()}
      
    </div>
  );
};

export default ProfileInfoSidebar;
